
'use client';

import { useState } from 'react';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { reviewActionErrorMessage, type ReviewAction } from '@/lib/review-action-errors';

type ReviewDialogProps = {
  /** Resolves once the review is stored; throws when it is not. */
  onSave: (review: { rating: number; text: string }) => Promise<void>;
  /** Resolves once the review is gone; throws when it is not. */
  onDelete: () => Promise<void>;
  onClose?: () => void;
  existingReview?: { rating: number; text: string };
  /**
   * The rating to start a NEW review on, when the dialog was opened from a star
   * in a review-request email. Ignored once the buyer has a review.
   */
  initialRating?: number;
};

export const StarRatingInput = ({ rating, setRating, disabled = false }: { rating: number, setRating: (r: number) => void, disabled?: boolean }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      setRating(Math.min(5, (rating || 0) + 1));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      setRating(Math.max(1, (rating || 1) - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setRating(1);
    } else if (e.key === 'End') {
      e.preventDefault();
      setRating(5);
    } else if (/^[1-5]$/.test(e.key)) {
      e.preventDefault();
      setRating(parseInt(e.key, 10));
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label="Star rating"
      aria-disabled={disabled || undefined}
      className="flex items-center gap-1"
      onKeyDown={handleKeyDown}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hoverRating || rating) >= star;
        const isChecked = rating === star;
        const tabIndexValue = rating === star || (rating === 0 && star === 1) ? 0 : -1;
        const ariaLabel = `${star} ${star === 1 ? 'star' : 'stars'}`;
        const className = "rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60";
        const onClick = () => { if (!disabled) setRating(star); };
        const onMouseEnter = () => { if (!disabled) setHoverRating(star); };
        const icon = (
          <Star
            className={`h-8 w-8 ${disabled ? '' : 'cursor-pointer '}transition-colors ${
              filled ? 'text-[hsl(var(--star))] fill-[hsl(var(--star))]' : 'text-muted-foreground'
            }`}
            aria-hidden
          />
        );
        // Two branches so the static a11y analyzer sees a literal aria-checked
        // value on each button rather than an expression it can't resolve.
        return isChecked ? (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked="true"
            aria-label={ariaLabel}
            tabIndex={tabIndexValue}
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={() => setHoverRating(0)}
            className={className}
          >
            {icon}
          </button>
        ) : (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked="false"
            aria-label={ariaLabel}
            tabIndex={tabIndexValue}
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={() => setHoverRating(0)}
            className={className}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
};

export default function ReviewDialog({ onSave, onDelete, onClose, existingReview, initialRating }: ReviewDialogProps) {
  const [rating, setRating] = useState(existingReview?.rating || initialRating || 0);
  const [text, setText] = useState(existingReview?.text || '');
  // The save or delete in flight. Everything that could start another one, or
  // change what is being saved, waits for it.
  const [pending, setPending] = useState<ReviewAction | null>(null);
  const busy = pending !== null;
  const { toast } = useToast();

  // Runs one save or delete to its end, and is the only place either outcome is
  // reported, for account and guest reviews alike. Success is claimed only once
  // the handler RESOLVES. This used to toast "Review Submitted!" and close the
  // moment Submit was clicked, so a refused write or a dropped connection still
  // said saved and threw the text away, and the guest handler toasted its own
  // result on top. A failure leaves the dialog open with the rating and text as
  // they were, to retry or cancel. Cancel stays usable meanwhile: closing does
  // not stop the write (a Firestore write made offline waits for the
  // connection), and its outcome is still toasted when it lands.
  const run = async (action: ReviewAction, perform: () => Promise<void>) => {
    if (pending) return;
    setPending(action);
    try {
      await perform();
    } catch (err) {
      setPending(null);
      toast({
        title: action === 'save' ? 'Could not save review' : 'Could not delete review',
        description: reviewActionErrorMessage(err, action),
        variant: 'destructive',
      });
      return;
    }
    setPending(null);
    toast(action === 'save'
      ? { title: 'Review submitted', description: 'Thank you for your feedback.' }
      : { title: 'Review deleted' });
    onClose?.();
  };

  const handleSave = () => {
    if (rating === 0) {
      toast({
        title: 'Please select a rating',
        variant: 'destructive',
      });
      return;
    }
    void run('save', () => onSave({ rating, text }));
  };

  // Returned to the confirm, which spins until run() has reported the outcome.
  const handleDelete = () => run('delete', onDelete);

  return (
    <DialogContent
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>{existingReview ? 'Edit your review' : 'Write a review'}</DialogTitle>
        <DialogDescription>
          Share your thoughts about this product with the community.
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-4">
        <div className="space-y-2">
            <Label>Your rating</Label>
            <StarRatingInput rating={rating} setRating={setRating} disabled={busy} />
        </div>
        <div className="space-y-2">
            <Label htmlFor="review-text">Your review <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
                id="review-text"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 500))}
                placeholder="What did you like or dislike?"
                rows={6}
                maxLength={500}
                disabled={busy}
            />
            <div className="flex items-center justify-between">
              {/* Not a DialogClose: closing on click hid the outcome, and a
                  failed delete had nowhere to show and nothing to retry from.
                  run() closes the dialog once the delete has landed. A review
                  is gone for good once deleted, so it asks first, like every
                  other permanent delete. */}
              {existingReview ? (
                <ConfirmDialog
                  trigger={
                    <button
                      type="button"
                      disabled={busy}
                      aria-busy={pending === 'delete' || undefined}
                      className="text-xs text-destructive/80 hover:text-destructive underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm disabled:opacity-60 disabled:pointer-events-none"
                    >
                      {pending === 'delete' ? 'Deleting…' : 'Delete review'}
                    </button>
                  }
                  title="Delete your review?"
                  description="Your rating and review are removed from this product for good."
                  confirmLabel="Delete review"
                  cancelLabel="Keep review"
                  onConfirm={handleDelete}
                />
              ) : <span />}
              <span className={`text-xs tabular-nums ${text.length > 500 ? 'text-destructive' : 'text-muted-foreground'}`}>{text.length}/500</span>
            </div>
        </div>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <Button onClick={handleSave} disabled={rating === 0 || busy} loading={pending === 'save'}>Submit review</Button>
      </DialogFooter>
    </DialogContent>
  );
}
